import { ModalViewModel } from '@/infra/ModalViewModel'
import { type MessageBankStore } from '@/features/messaging/message-bank/MessageBankStore'
import { type MessageBankReply } from '@/features/messaging/message-bank/MessageBankReply'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import type { Id } from '@/infra/id'

/**
 * MessageBank Dialog view model.
 */
export class MessageBankDialogViewModel {
  @observable private accessor _selectedReply: MessageBankReply | null = null

  private _abortController: AbortController | null = null

  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({
    onClose: () => this._abortController?.abort(),
  })

  constructor(
    private readonly _getWorkspaceId: () => Id,
    private readonly _onMessageBankReplySelected: (
      replyContent: string,
    ) => void,
    private readonly _messageBankStore: MessageBankStore,
  ) {}

  /**
   * The workspace whose message bank is shown.
   */
  @computed
  get workspaceId() {
    return this._getWorkspaceId()
  }

  /**
   * The selected reply, defaulting to the first one.
   */
  @computed
  get selectedReply() {
    return this._selectedReply ?? this.messageBankReplies[0] ?? null
  }

  /**
   * The replies in the message bank.
   */
  @computed
  get messageBankReplies(): MessageBankReply[] {
    if (this.messageBank) {
      return this.messageBank.replies.items
    }

    return []
  }

  /**
   * The message bank for the job's workspace.
   */
  @computed
  get messageBank() {
    return this._messageBankStore.getForWorkspace(this.workspaceId)
  }

  /**
   * Whether the message bank has any replies.
   */
  @computed
  get messageBankHasReplies() {
    return this.messageBankReplies.length > 0
  }

  /**
   * The replies as shown in the list.
   */
  @computed
  get replyItems() {
    return this.messageBankReplies.map(toReplyItem)
  }

  /**
   * The selected reply as shown in the detail pane.
   */
  @computed
  get selectedReplyItem() {
    return this.selectedReply ? toReplyItem(this.selectedReply) : null
  }

  /**
   * Selects a reply to preview.
   */
  @action.bound
  selectReply(replyId: Id) {
    const reply = this.messageBankReplies.find((r) => r.id === replyId)
    if (reply) {
      this._selectedReply = reply
    }
  }

  /**
   * Inserts the selected reply into the composer and closes the dialog.
   */
  @action.bound
  useSelectedReply() {
    if (!this.selectedReply) {
      return
    }
    this._onMessageBankReplySelected(this.selectedReply.content)
    this.modal.close()
  }

  /**
   * Shows the dialog and loads the workspace's message bank into it.
   */
  @action.bound
  show() {
    this._abortController?.abort()
    this._abortController = new AbortController()
    this.modal.show()
    // A workspace without a configured bank fails the fetch, which the dialog shows as having no replies.
    this.fetch(this._abortController.signal).catch(() => undefined)
  }

  /**
   * Loads the workspace's message bank and its replies.
   */
  fetch = task.resolved(async (signal: AbortSignal) => {
    const messageBank = await this._messageBankStore.fetchWorkspaceMessageBank(
      this.workspaceId,
      signal,
    )
    // Without its replies the bank shows as having none.
    await this._messageBankStore
      .fetchMessageBankReplies(messageBank.id, signal)
      .catch(() => undefined)
  })
}

function toReplyItem(reply: MessageBankReply) {
  return { id: reply.id, title: reply.title, content: reply.content }
}
